import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Translation } from '@/translations/types';
import { languageFromPath, type Language } from '@/lib/languageFromPath';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Translation | null;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Cache for loaded translations to avoid re-fetching
const translationsCache: Partial<Record<Language, Translation>> = {};

// Preload Spanish as it's the default — populates the cache so loadTranslations('es')
// resolves from cache on first render without a loading flicker.
void (async () => {
  if (!translationsCache.es) {
    const module = await import('@/translations/es');
    translationsCache.es = module.es;
  }
})();

interface LanguageProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider = ({ children, defaultLanguage = 'es' }: LanguageProviderProps) => {
  // Al entrar directamente en /en/about la primera pintura ya sale en inglés; después,
  // useLanguageFromUrl (en RootLayout) sigue a la URL en cada navegación
  const [language, setLanguageState] = useState<Language>(() =>
    typeof window === 'undefined' ? defaultLanguage : languageFromPath(window.location.pathname)
  );
  const [translations, setTranslations] = useState<Translation | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadTranslations = useCallback(async (lang: Language) => {
    // Check cache first
    if (translationsCache[lang]) {
      setTranslations(translationsCache[lang]!);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      let module: { es?: Translation; en?: Translation; pt?: Translation; fr?: Translation };
      
      switch (lang) {
        case 'es':
          module = await import('@/translations/es');
          translationsCache.es = module.es;
          break;
        case 'en':
          module = await import('@/translations/en');
          translationsCache.en = module.en;
          break;
        case 'pt':
          module = await import('@/translations/pt');
          translationsCache.pt = module.pt;
          break;
        case 'fr':
          module = await import('@/translations/fr');
          translationsCache.fr = module.fr;
          break;
      }

      setTranslations(translationsCache[lang]!);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      // Fallback to Spanish if loading fails
      if (lang !== 'es') {
        const esModule = await import('@/translations/es');
        translationsCache.es = esModule.es;
        setTranslations(translationsCache.es);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  // Load translations when language changes
  // (loadTranslations handles the cache, and preloadDefaultLanguage above primes cache.es
  // so Spanish resolves synchronously on first render.)
  useEffect(() => {
    loadTranslations(language);
  }, [language, loadTranslations]);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translations, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Hook that provides translations with fallback
export const useTranslations = () => {
  const { translations, isLoading } = useLanguage();
  return { t: translations, isLoading };
};
